import { useState, type ImgHTMLAttributes } from 'react';

import { clsx } from 'clsx';
import { Spinner } from '@cian/ui-kit';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import * as styles from './styles.css';
import { TDeprecatedColors } from '@cian/ui-kit-design-tokens/colors/deprecated';
import { TColors } from '@cian/ui-kit-design-tokens/colors';

export interface ILazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  loaderColor?: TColors | TDeprecatedColors;
  className?: string;
  wrapperClassName?: string;
  wrapperLoadingClassName?: string;
}

export const LazyImage: React.FC<ILazyImageProps> = ({
  src,
  alt,
  loaderColor = 'icon-secondary-default',
  className,
  wrapperClassName,
  wrapperLoadingClassName,
  onLoad,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true);

    onLoad?.(e);
  };

  const spinner = (
    <div className={styles['placeholder']} aria-hidden>
      <Spinner size={24} color={loaderColor} />
    </div>
  );

  const lazyPlaceholder = (
    <div
      className={clsx(styles['wrapper'], styles['wrapper-lazy-placeholder'], wrapperClassName, wrapperLoadingClassName)}
    >
      {spinner}
    </div>
  );

  return (
    <LazyLoadComponent placeholder={lazyPlaceholder}>
      <div
        className={clsx(
          styles['wrapper'],
          !loaded && styles['wrapper-lazy-placeholder'],
          wrapperClassName,
          !loaded && wrapperLoadingClassName,
        )}
      >
        <img
          src={src}
          alt={alt}
          className={clsx(styles['image'], className, loaded && styles['image-loaded'])}
          onLoad={handleLoad}
          {...rest}
        />
        {!loaded && spinner}
      </div>
    </LazyLoadComponent>
  );
};
